import { AdapterType, MESSAGE_LOG_MAX } from '@k-ucap/shared';
import type { MessageInstance, AdapterStats } from '@k-ucap/shared';
import type { MessageTransportAdapter, AdapterStatus } from './MessageTransportAdapter.js';
import { useIntegrationStore } from '@/stores/useIntegrationStore.js';

export class VirtualMessageAdapter implements MessageTransportAdapter {
  private status: AdapterStatus = 'connected';
  private handlers: Set<(message: MessageInstance) => void> = new Set();
  private history: MessageInstance[] = [];
  private stats: AdapterStats = {
    rxCount: 0,
    txCount: 0,
    rxBytes: 0,
    txBytes: 0,
    errors: 0,
    reconnects: 0,
  };
  private statusHandlers: Set<(status: AdapterStatus, error?: string) => void> = new Set();

  constructor() {
    useIntegrationStore.getState().registerAdapter(this.getName(), this.getName(), AdapterType.VIRTUAL);
    useIntegrationStore.getState().updateAdapterStatus(this.getName(), 'connected');
  }

  getName(): string {
    return 'VirtualMessageAdapter';
  }

  async connect(): Promise<void> {
    this.status = 'connected';
    this.notifyStatus('connected');
  }

  async disconnect(): Promise<void> {
    this.status = 'disconnected';
    this.notifyStatus('disconnected');
  }

  async publish(message: MessageInstance): Promise<void> {
    this.history.push(message);
    if (this.history.length > MESSAGE_LOG_MAX) {
      this.history.shift();
    }
    this.stats.txCount++;
    this.stats.txBytes += JSON.stringify(message).length;
    useIntegrationStore
      .getState()
      .recordMessage(this.getName(), 'TX', message, message.validation === 'FAIL' ? 'error' : 'ok');
    this.handlers.forEach((handler) => handler(message));
  }

  subscribe(handler: (message: MessageInstance) => void): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  getStatus(): AdapterStatus {
    return this.status;
  }

  getStats(): AdapterStats {
    return { ...this.stats };
  }

  onStatusChange(callback: (status: AdapterStatus, error?: string) => void): () => void {
    this.statusHandlers.add(callback);
    return () => this.statusHandlers.delete(callback);
  }

  private notifyStatus(status: AdapterStatus, error?: string): void {
    this.statusHandlers.forEach((h) => h(status, error));
  }

  getHistory(): MessageInstance[] {
    return [...this.history];
  }

  clearHistory(): void {
    this.history = [];
  }
}

export const createVirtualMessageAdapter = (): VirtualMessageAdapter => new VirtualMessageAdapter();

let globalVirtualAdapter: VirtualMessageAdapter | null = null;

export const getGlobalVirtualMessageAdapter = (): VirtualMessageAdapter => {
  if (!globalVirtualAdapter) {
    globalVirtualAdapter = createVirtualMessageAdapter();
    globalVirtualAdapter.connect().catch(() => {
      // ignore
    });
  }
  return globalVirtualAdapter;
};
